import { useState, useContext } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { ReactComponent as PlusSquareFill } from "bootstrap-icons/icons/plus-square-fill.svg";
import { ReactComponent as PencilSquare } from "bootstrap-icons/icons/pencil-square.svg";
import { ReactComponent as Eye } from "bootstrap-icons/icons/eye.svg";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash3.svg";
import { ReactComponent as ChevronRightIcon } from "bootstrap-icons/icons/chevron-right.svg";
import Spinner from "../spinner/Spinner";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import CustomStyles from "../../components/utils/CustomStyles";
import { AuthContext } from "../../AuthProvider/AuthProvider";
import Swal from "sweetalert2";
import { useCommissions, useDeleteCommission } from "../../hooks/useAppData";
import AxiosApiCall from "../../api/AxiosApiCall";
import ApiServices from "../../api/ApiServices";
import { handleError } from "../../components/utils/AuthenticationCheck";

const CommissionList = () => {
  const [filterText, setFilterText] = useState("");
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const { permissions } = useContext(AuthContext);

  const navigate = useNavigate();
  const axios = AxiosApiCall();

  const { data: commissionsResponse, isLoading: loading, refetch } = useCommissions();
  const commissions = commissionsResponse?.data || [];
  const deleteCommissionMutation = useDeleteCommission();

  const handleFilter = (e) => {
    setFilterText(e.target.value);
    setResetPaginationToggle(!resetPaginationToggle);
  };

  // Format pricing rules for display
  const formatPricingRules = (pricingRules) => {
    if (!pricingRules) return "NOT AVAILABLE";

    return Object.entries(pricingRules)
      .map(([network, rule]) => `${network}: ${rule.value}%`)
      .join(", ");
  };

  // Format status with badge
  const formatStatus = (isActive) => {
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${isActive ? "bg-darkGreen3 text-lGrey" : "bg-dash3 text-lGrey"
        }`}>
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const handleDeleteCommission = async (id) => {

    const authData = JSON.parse(localStorage.getItem("auth"));
    const token = authData?.token;
    if (!token) {
      throw new Error("No access token found.");
    }

    try {
      const response = await axios.delete(
        ApiServices.deletePhysicalProductCom,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          data: { id },
          withCredentials: true,
        }
      );


      const { status, message } = response.data;
      if (status === 200) {
        toast.success(message);
        refetch();
      } else {
        handleError({ response: { status, data: { message } } }, navigate);
      }
    } catch (error) {
      handleError(error, navigate);
    }
  };


  const handleDeleteConfirmation = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDeleteCommission(id);
      }
    });
  };

  // Define Expandable Component for Actions
  const ExpandableComponent = ({ data }) => (
    <div className="flex justify-content-start">
      {permissions.some(permission => permission.name === 'admin.view') && (
        <Link
          to="/admin/commission/details"
          state={{ commission: data }}
          className="mx-2 my-2 group"
        >
          <Eye className="w-5 h-5 text-darkGreen3" />
        </Link>
      )}
      {permissions.some(permission => permission.name === 'admin.edit') && (
        <Link
          to="/admin/commission/update"
          state={{ commission: data }}
          className="mx-2 my-2 group"
        >
          <PencilSquare className="w-5 h-5 text-dash4" />
        </Link>
      )}
      {/* {permissions.some(permission => permission.name === '') && ( */}
      <button
        className="mx-2 my-2 group"
        onClick={() => handleDeleteConfirmation(data.id)}
      >
        <Trash className="w-5 h-5 text-dash3" />
      </button>
      {/* )} */}
    </div>
  );

  const columns = [
    {
      name: "Date",
      selector: (row) => new Date(row.created_at).toLocaleDateString(),
      sortable: true,
      width: "150px",
    },
    {
      name: "Name",
      selector: (row) => row.name ? row.name : "NOT AVAILABLE",
      sortable: true,
      width: "150px",
    },
    {
      name: "Product",
      selector: (row) => row.branch_product_unit_name ? row.branch_product_unit_name : "NOT AVAILABLE",
      sortable: true,
      width: "200px",
    },
    {
      name: "Discount %",
      selector: (row) => row.discount_percentage ? `${row.discount_percentage}%` : "0%",
      sortable: true,
      width: "120px",
    },
    {
      name: "Status",
      cell: (row) => formatStatus(row.is_active) ? formatStatus(row.is_active) : "NOT AVAILABLE",
      sortable: true,
      width: "100px",
    },

    // {
    //   name: "Pricing Rules",
    //   selector: (row) => formatPricingRules(row.pricing_rules) ? formatPricingRules(row.pricing_rules) : "NOT AVAILABLE",
    //   sortable: false,
    //   wrap: true,
    //   width: "250px",
    // },


    {
      name: "Description",
      selector: (row) => row.description ? row.description : "NOT AVAILABLE",
      sortable: true,
      wrap: true,
      width: "300px",
    },
  ];

  const filteredItems = commissions.filter((item) => {
    const searchTerm = filterText.toLowerCase();

    return (
      (item.name && item.name.toLowerCase().includes(searchTerm)) ||
      (item.description && item.description.toLowerCase().includes(searchTerm)) ||
      (item.discount_percentage && item.discount_percentage.toString().includes(searchTerm)) ||
      (item.pricing_rules && JSON.stringify(item.pricing_rules).toLowerCase().includes(searchTerm))
    );
  });

  const data = filteredItems.map((commission) => ({
    id: commission.id,
    name: commission.name.toUpperCase() || "NOT AVAILABLE",
    description: commission.description.toUpperCase() || "NOT AVAILABLE",
    discount_percentage: commission.discount_percentage || "0",
    pricing_rules: commission.pricing_rules || {},
    is_active: commission.is_active,
    created_at: commission.created_at,
    branch_product_unit_id: commission.branch_product_unit_id,
    branch_product_unit_name: commission.branch_product_unit_name || "NOT AVAILABLE",
  }));

  // Sort data based on creation date (newest first)
  const sortedData = [...data].sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  return (
    <div>
      <MainLayouts>
        <div className="relative overflow-x-auto sm:rounded-lg py-6 w-full">
          <section
            className="w-full pr-[3%] pt-3 my-3"
            style={{ backgroundColor: "#F7F7F7" }}
          >
            <nav className="bg-gray-200 py-0 px-4 rounded-lg">
              <ol className="list-none p-0 inline-flex mb-2">
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Physical Product
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-blue-500 hover:text-blue-600 text-darkGreen3 pr-2">
                    Commission
                  </p>
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <p className="text-orange1">List</p>
                </li>
              </ol>
            </nav>
          </section>

          <div className="pb-4 bg-white dark:bg-gray-900 flex flex-wrap justify-between">
            <div className="relative w-full sm:w-1/2 mb-4 sm:mb-0">
              <input
                type="search"
                className="block w-full px-3 py-2 text-md text-gray-900 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                placeholder="Search by name, description, or percentage..."
                value={filterText}
                onChange={handleFilter}
              />
            </div>
            {permissions.some(permission => permission.name === 'admin.create') && (
              <Link
                to="/admin/commission/create"
                className="w-full sm:w-auto text-dGrey3 bg-darkGreen3 hover:bg-green-700 focus:ring-4 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center transition-colors duration-200"
              >
                <span className="flex justify-center sm:justify-start mx-2">
                  <PlusSquareFill className="mr-2" />
                  Create Commission
                </span>
              </Link>
            )}
          </div>

          {loading ? (
            <Spinner loading={loading} />
          ) : (
            <DataTable
              columns={columns}
              data={sortedData}
              pagination
              highlightOnHover
              striped
              customStyles={CustomStyles}
              noDataComponent={<div className="py-4 text-center">No commissions found</div>}
              expandableRows
              expandableRowsComponent={ExpandableComponent}
            />
          )}
        </div>
      </MainLayouts>
    </div>
  );
};

export default CommissionList;